import { useState, useEffect, useMemo, useCallback } from 'react'
import { createPortal } from 'react-dom'
import moment from 'moment'
import toast from 'react-hot-toast'
import { ImportMatchesModal } from '@/components/modal'
import { MatchByPlayType } from '@/components/match/MatchByPlayType'
import { useGetClubMatches, useDeleteClubMatch } from '@/services/club'
import { useClubEditMatch } from '@/services/match'
import { useAppSelector } from '@/hooks/useRedux'
import { userSelector } from '@/pages/user/selectors'
import { Match, PayloadDeleteClubMatch, Team } from '@/types'
import { isExternallyManagedMatch } from '@/constants/Common'
import { useTranslation } from 'react-i18next'
import { MatchFilterPopover, MatchFilterState, EMPTY_MATCH_FILTER, countActiveMatchFilters, describeActiveMatchFilters } from './MatchFilterPopover'
import { TabBar, Spinner, EmptyState, CMButton } from './ui'

interface ClubMatchesTabProps {
  clubId: number
  isDirector: boolean
}

const PAGE_SIZE = 20

type FormatFilter = 'ALL' | 'SINGLES' | 'DOUBLES'
type SortField = 'date' | 'event'
type SortDir = 'asc' | 'desc'

const FORMAT_TABS: { id: FormatFilter; label: string }[] = [
  { id: 'ALL', label: 'All' },
  { id: 'SINGLES', label: 'Singles' },
  { id: 'DOUBLES', label: 'Doubles' },
]

const SortArrow = ({ dir }: { dir: SortDir | null }) => {
  if (!dir) return <span className="text-gray-300 ml-0.5">↕</span>
  return <span className="text-primary-500 ml-0.5">{dir === 'asc' ? '↑' : '↓'}</span>
}

/* ─── Helper: extract games from teams ─── */
function getGames(team1: Team, team2: Team): [number, number][] {
  const games: [number, number][] = []
  for (let i = 1; i <= 5; i++) {
    const g1 = (team1 as any)[`game${i}`]
    const g2 = (team2 as any)[`game${i}`]
    if (g1 !== undefined && g1 !== -1 && g2 !== undefined && g2 !== -1) {
      games.push([g1, g2])
    }
  }
  return games
}

function formatPlayers(team: Team): string {
  if (team.player2) return `${team.player1.fullName} & ${team.player2.fullName}`
  return team.player1.fullName
}

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   Portal dialog — renders above the z-40 club management modal
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
const PortalDialog = ({
  open,
  onClose,
  children,
  width = 'max-w-lg',
}: {
  open: boolean
  onClose: () => void
  children: React.ReactNode
  width?: string
}) => {
  if (!open) return null
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className={`relative bg-white rounded-lg shadow-2xl ${width} w-full max-h-[80vh] overflow-auto`}>
        {children}
      </div>
    </div>,
    document.body
  )
}

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   Main component
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export const ClubMatchesTab = ({ clubId, isDirector }: ClubMatchesTabProps) => {
  const { t } = useTranslation()
  const { user } = useAppSelector(userSelector)

  const { mutate: getClubMatches, data: matchResponse, isLoading } = useGetClubMatches()
  const { mutate: deleteClubMatch, isLoading: isDeleting } = useDeleteClubMatch()
  const { mutateAsync: editClubMatch, isLoading: isEditing } = useClubEditMatch()

  const [matches, setMatches] = useState<Match[]>([])
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [formatFilter, setFormatFilter] = useState<FormatFilter>('ALL')
  const [showImport, setShowImport] = useState(false)
  const [searchEvent, setSearchEvent] = useState('')

  // Dialog state
  const [detailMatch, setDetailMatch] = useState<Match | null>(null)
  const [editMatch, setEditMatch] = useState<Match | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Match | null>(null)

  // Client sort
  const [clientSort, setClientSort] = useState<{ field: SortField; dir: SortDir }>({ field: 'date', dir: 'desc' })

  // Filter state
  const [filter, setFilter] = useState<MatchFilterState>(EMPTY_MATCH_FILTER)

  // Edit mode state (manage matches)
  const [isEditMode, setIsEditMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [isBulkDeleting, setIsBulkDeleting] = useState(false)

  const doFetch = (fetchOffset: number) => {
    const filters: any = {}
    if (formatFilter !== 'ALL') filters.eventFormat = [formatFilter]
    if (searchEvent.trim()) filters.eventName = searchEvent.trim()
    if (filter.dateFrom || filter.dateTo) {
      filters.eventDate = {
        startDate: filter.dateFrom || '2000-01-01',
        endDate: filter.dateTo || '2099-12-31',
      }
    }
    if (filter.matchTypes.length > 0) {
      filters.matchType = filter.matchTypes
    }
    if (filter.playerId) {
      filters.playerId = filter.playerId
    }
    getClubMatches({
      clubId,
      filters,
      limit: PAGE_SIZE,
      offset: fetchOffset,
      sort: { order: 'DESC', parameter: 'MATCH_DATE' },
    })
  }

  // Fetch on mount and when server-side filters change
  useEffect(() => {
    setMatches([])
    setOffset(0)
    doFetch(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId, formatFilter, searchEvent, filter.dateFrom, filter.dateTo, filter.matchTypes, filter.playerId])

  // Accumulate matches from response
  useEffect(() => {
    if (matchResponse) {
      if (offset === 0) {
        setMatches(matchResponse.result.hits)
      } else {
        setMatches((prev) => [...prev, ...matchResponse.result.hits])
      }
      setHasMore(matchResponse.result.hasMore)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchResponse])

  const handleLoadMore = () => {
    const newOffset = offset + PAGE_SIZE
    setOffset(newOffset)
    doFetch(newOffset)
  }

  // All match filters are now server-side (date, matchType, playerId)
  const filteredMatches = matches

  // Client-side sort
  const sortedMatches = useMemo(() => {
    const { field, dir } = clientSort
    return [...filteredMatches].sort((a, b) => {
      let aVal: string | number, bVal: string | number
      switch (field) {
        case 'date':
          aVal = new Date(a.eventDate).getTime()
          bVal = new Date(b.eventDate).getTime()
          break
        case 'event':
          aVal = (a.eventName || '').toLowerCase()
          bVal = (b.eventName || '').toLowerCase()
          break
        default:
          return 0
      }
      if (aVal < bVal) return dir === 'asc' ? -1 : 1
      if (aVal > bVal) return dir === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredMatches, clientSort])

  // Externally-managed matches (non-CLUB source) can't be edited/deleted here,
  // so they're never selectable for bulk actions.
  const selectableMatches = useMemo(
    () => sortedMatches.filter((m) => !isExternallyManagedMatch(m.matchSource)),
    [sortedMatches]
  )

  const toggleSort = (field: SortField) => {
    setClientSort((prev) => {
      if (prev.field === field) return { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      return { field, dir: field === 'date' ? 'desc' : 'asc' }
    })
  }

  /* ── Delete handler (single match) ── */
  const handleDelete = () => {
    if (!deleteTarget) return
    const payload: PayloadDeleteClubMatch = {
      clubId,
      matchId: deleteTarget.id,
      matchIdType: 'OBFUSCATED_MATCH_ID',
      reason: 'Match Deleted by Director',
      requestedByDuprId: user?.referralCode as string,
    }
    deleteClubMatch(payload, {
      onSuccess: () => {
        toast.success('Match deleted')
        setDeleteTarget(null)
        setMatches([])
        setOffset(0)
        doFetch(0)
      },
      onError: () => toast.error('Failed to delete match'),
    })
  }

  /* ── Bulk delete handler ── */
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return
    setIsBulkDeleting(true)

    const ids = Array.from(selectedIds)
    let successCount = 0
    let failCount = 0

    for (const matchId of ids) {
      try {
        await new Promise<void>((resolve, reject) => {
          const payload: PayloadDeleteClubMatch = {
            clubId,
            matchId,
            matchIdType: 'OBFUSCATED_MATCH_ID',
            reason: 'Bulk Delete by Director',
            requestedByDuprId: user?.referralCode as string,
          }
          deleteClubMatch(payload, {
            onSuccess: () => { successCount++; resolve() },
            onError: () => { failCount++; reject() },
          })
        })
      } catch {
        // counted in failCount
      }
    }

    setIsBulkDeleting(false)
    if (successCount > 0) toast.success(`Deleted ${successCount} match${successCount > 1 ? 'es' : ''}`)
    if (failCount > 0) toast.error(`Failed to delete ${failCount} match${failCount > 1 ? 'es' : ''}`)
    exitEditMode()
    setMatches([])
    setOffset(0)
    doFetch(0)
  }

  /* ── Refresh after import ── */
  const handleRefreshAfterImport = () => {
    setMatches([])
    setOffset(0)
    doFetch(0)
  }

  const copyMatchId = (match: Match, e?: React.MouseEvent) => {
    e?.stopPropagation()
    navigator.clipboard.writeText(match.displayIdentity)
    toast.success('Match ID copied')
  }

  /* ── Edit mode helpers ── */
  const exitEditMode = () => {
    setIsEditMode(false)
    setSelectedIds(new Set())
  }

  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(selectableMatches.map((m) => m.id)))
    } else {
      setSelectedIds(new Set())
    }
  }, [selectableMatches])

  const handleToggleSelect = useCallback((matchId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(matchId)) next.delete(matchId)
      else next.add(matchId)
      return next
    })
  }, [])

  const allVisibleSelected = selectableMatches.length > 0 && selectableMatches.every((m) => selectedIds.has(m.id))

  const filterCount = useMemo(() => countActiveMatchFilters(filter), [filter])
  const filterChips = useMemo(() => describeActiveMatchFilters(filter), [filter])
  const anyFilterActive = searchEvent || filterCount > 0

  const totalShown = matches.length

  const formatCounts = useMemo(() => ({
    ALL: matchResponse?.result?.total,
    SINGLES: undefined,
    DOUBLES: undefined,
  }), [matchResponse])

  const COLUMNS: { id: SortField | 'format' | 'players' | 'score' | 'actions'; label: string; sortable: boolean; className?: string }[] = [
    { id: 'date', label: 'Date', sortable: true },
    { id: 'event', label: 'Event', sortable: true },
    { id: 'format', label: 'Format', sortable: false, className: 'text-center' },
    { id: 'players', label: 'Players', sortable: false },
    { id: 'score', label: 'Score', sortable: false, className: 'text-center' },
    ...(isDirector ? [{ id: 'actions' as const, label: '', sortable: false, className: 'w-20' }] : []),
  ]

  return (
    <div className="space-y-3">
      {/* Toolbar: format tabs + actions */}
      <div className="flex items-center justify-between gap-2">
        <TabBar
          tabs={FORMAT_TABS.map(({ id, label }) => ({
            id,
            label,
            count: formatCounts[id],
          }))}
          activeTab={formatFilter}
          onTabChange={(id) => setFormatFilter(id as FormatFilter)}
          size="sm"
        />
        <div className="flex-1" />
        <div className="flex items-center gap-2 flex-shrink-0">
          {isDirector && (
            <button
              onClick={() => setShowImport(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 transition-colors"
            >
              <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Import Matches
            </button>
          )}
          {isDirector && !isEditMode && (
            <button
              onClick={() => setIsEditMode(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
            >
              Manage Matches
            </button>
          )}
        </div>
      </div>

      {/* Edit mode toolbar */}
      {isEditMode && (
        <div className="flex items-center justify-between gap-2 px-2 py-1.5 bg-gray-50 border border-gray-200 text-xs">
          <span className="text-gray-600">
            {selectedIds.size} selected
          </span>
          <div className="flex items-center gap-2">
            <CMButton
              variant="danger"
              size="sm"
              onClick={handleBulkDelete}
              disabled={selectedIds.size === 0 || isBulkDeleting}
              className="text-xs py-1"
            >
              {isBulkDeleting ? 'Deleting...' : `Delete (${selectedIds.size})`}
            </CMButton>
            <CMButton variant="muted" size="sm" onClick={exitEditMode} className="text-xs py-1">
              Cancel
            </CMButton>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex gap-2 items-center">
        <div className="relative flex-1">
          <svg className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Filter by event name..."
            value={searchEvent}
            onChange={(e) => setSearchEvent(e.target.value)}
            className="w-full pl-7 pr-3 py-1.5 border border-gray-200 text-xs text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
          />
        </div>
        <MatchFilterPopover filter={filter} onChange={setFilter} clubId={clubId} />
      </div>

      {/* Active filter chips */}
      {filterChips.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {filterChips.map((chip) => (
            <span key={chip} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-primary-50 border border-primary-200 text-[10px] text-primary-700">
              {chip}
            </span>
          ))}
          <button
            onClick={() => setFilter(EMPTY_MATCH_FILTER)}
            className="text-[10px] text-gray-400 hover:text-gray-600 px-1"
          >
            clear
          </button>
        </div>
      )}

      {/* Match table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              {isEditMode && (
                <th className="py-1.5 px-2 w-8">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="w-3.5 h-3.5 accent-primary-600"
                  />
                </th>
              )}
              {COLUMNS.map((col) => (
                <th
                  key={col.id}
                  onClick={col.sortable ? () => toggleSort(col.id as SortField) : undefined}
                  className={`text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider py-1.5 px-2 select-none ${
                    col.sortable ? 'cursor-pointer hover:text-gray-700' : ''
                  } ${col.className || ''}`}
                >
                  {col.label}
                  {col.sortable && <SortArrow dir={clientSort.field === col.id ? clientSort.dir : null} />}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedMatches.map((match) => {
              if (match.matchPlayType === 'INFORMATIONAL' || match.matchPlayType === 'ADJUSTMENT') {
                const colCount = COLUMNS.length + (isEditMode ? 1 : 0)
                return (
                  <tr key={match.id} className="border-b border-gray-50">
                    <td colSpan={colCount} className="py-2 px-2">
                      <MatchByPlayType
                        matchPlayType={match.matchPlayType}
                        matchDate={match.eventDate}
                        adjustmentData={{ eventName: match.eventName, match }}
                        regular={() => null}
                      />
                    </td>
                  </tr>
                )
              }

              const [team1, team2] = [...match.teams].sort((a, b) => a.serial - b.serial)
              const games = getGames(team1, team2)
              const externallyManaged = isExternallyManagedMatch(match.matchSource)
              const dimmed = isEditMode && externallyManaged

              return (
                <tr
                  key={match.id}
                  onClick={() => !isEditMode && setDetailMatch(match)}
                  className={`border-b border-gray-50 hover:bg-gray-50 group ${isEditMode ? '' : 'cursor-pointer'} ${dimmed ? 'opacity-50 bg-gray-50' : ''}`}
                >
                  {isEditMode && (
                    <td className="py-2 px-2 w-8">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(match.id)}
                        disabled={externallyManaged}
                        onChange={() => handleToggleSelect(match.id)}
                        className={`w-3.5 h-3.5 accent-primary-600 ${externallyManaged ? 'cursor-not-allowed opacity-40' : ''}`}
                      />
                    </td>
                  )}
                  <td className="py-2 px-2 w-24">
                    <span className="text-xs text-gray-600">{moment(match.eventDate).format('MMM D, YYYY')}</span>
                  </td>
                  <td className="py-2 px-2">
                    <span className="text-xs text-gray-800 truncate block max-w-[180px]">{match.eventName || '—'}</span>
                    {dimmed && (
                      <span className="text-[10px] text-gray-400 italic block max-w-[180px] leading-tight mt-0.5">
                        {t('common.externalMatchManaged')}
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-2 text-center">
                    <span className={`inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold ${
                      match.eventFormat === 'DOUBLES' ? 'bg-blue-50 text-blue-600' : 'bg-amber-50 text-amber-600'
                    }`}>
                      {match.eventFormat === 'DOUBLES' ? 'D' : 'S'}
                    </span>
                  </td>
                  <td className="py-2 px-2">
                    <div className="text-xs text-gray-700">
                      <span>{formatPlayers(team1)}</span>
                      <span className="text-gray-400 mx-1">vs</span>
                      <span>{formatPlayers(team2)}</span>
                    </div>
                  </td>
                  <td className="py-2 px-2 text-center">
                    <div className="flex gap-1.5 justify-center">
                      {games.map(([g1, g2], i) => (
                        <span key={i} className="text-xs font-mono text-gray-700">
                          <span className={g1 > g2 ? 'font-semibold' : 'text-gray-400'}>{g1}</span>
                          <span className="text-gray-300">-</span>
                          <span className={g2 > g1 ? 'font-semibold' : 'text-gray-400'}>{g2}</span>
                        </span>
                      ))}
                    </div>
                  </td>
                  {isDirector && !isEditMode && (
                    <td className="py-2 px-2 w-20">
                      <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity justify-end">
                        {/* Copy */}
                        <button
                          onClick={(e) => copyMatchId(match, e)}
                          title="Copy match ID"
                          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                        {/* Edit */}
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditMatch(match) }}
                          title="Edit match"
                          className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        {/* Delete */}
                        <button
                          onClick={(e) => { e.stopPropagation(); setDeleteTarget(match) }}
                          title="Delete match"
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  )}
                  {isDirector && isEditMode && (
                    <td className="py-2 px-2 w-20" />
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>

        {isLoading && (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        )}

        {!isLoading && sortedMatches.length === 0 && (
          <EmptyState
            icon={
              <svg className="w-10 h-10 text-gray-300 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            }
            message={anyFilterActive ? 'No matches match your filters' : 'No matches found'}
            className="py-8"
          />
        )}
      </div>

      {/* Pagination */}
      {totalShown > 0 && (
        <div className="flex items-center justify-between pt-1 border-t border-gray-100">
          <span className="text-xs text-gray-500">
            {filteredMatches.length !== matches.length
              ? `${filteredMatches.length} of ${totalShown} match${totalShown !== 1 ? 'es' : ''} shown`
              : `${totalShown} match${totalShown !== 1 ? 'es' : ''} shown`
            }
            {matchResponse?.result?.total !== undefined && ` (${matchResponse.result.total} total)`}
          </span>
          {hasMore && (
            <button
              onClick={handleLoadMore}
              disabled={isLoading}
              className="px-2 py-1 border border-gray-200 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Load more
            </button>
          )}
        </div>
      )}

      {/* ── Import Matches Modal (portal via Headless UI, but needs z-50 wrapper) ── */}
      {showImport && createPortal(
        <div className="relative z-50">
          <ImportMatchesModal
            open={showImport}
            onClose={() => setShowImport(false)}
            clubId={String(clubId)}
            onRefresh={handleRefreshAfterImport}
          />
        </div>,
        document.body
      )}

      {/* ── Match Detail Dialog ── */}
      <MatchDetailDialog
        match={detailMatch}
        isDirector={isDirector}
        onClose={() => setDetailMatch(null)}
        onEdit={(m) => { setDetailMatch(null); setEditMatch(m) }}
        onDelete={(m) => { setDetailMatch(null); setDeleteTarget(m) }}
        onCopyId={copyMatchId}
      />

      {/* ── Edit Match Dialog ── */}
      <EditMatchDialog
        match={editMatch}
        clubId={clubId}
        isEditing={isEditing}
        editClubMatch={editClubMatch}
        user={user}
        onClose={() => setEditMatch(null)}
        onSuccess={() => {
          setEditMatch(null)
          setMatches([])
          setOffset(0)
          doFetch(0)
        }}
      />

      {/* ── Delete Confirmation Dialog ── */}
      <PortalDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} width="max-w-sm">
        <div className="p-5">
          <h3 className="text-base font-semibold text-gray-900 mb-2">Delete Match</h3>
          <p className="text-sm text-gray-600 mb-5">
            Are you sure you want to delete this match? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setDeleteTarget(null)}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </PortalDialog>
    </div>
  )
}

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   Match Detail Dialog
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
const MatchDetailDialog = ({
  match,
  isDirector,
  onClose,
  onEdit,
  onDelete,
  onCopyId,
}: {
  match: Match | null
  isDirector: boolean
  onClose: () => void
  onEdit: (m: Match) => void
  onDelete: (m: Match) => void
  onCopyId: (m: Match) => void
}) => {
  if (!match) return null

  const [team1, team2] = [...match.teams].sort((a, b) => a.serial - b.serial)
  const games = getGames(team1, team2)

  const renderTeamDetail = (team: Team, label: string, isWinner: boolean) => (
    <div className={`flex-1 p-3 border ${isWinner ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
      <div className="flex items-center gap-1 mb-1">
        <span className="text-[10px] font-medium text-gray-400 uppercase">{label}</span>
        {isWinner && <span className="text-[10px] font-semibold text-green-600">W</span>}
      </div>
      <p className="text-sm font-medium text-gray-900">{team.player1.fullName}</p>
      {team.player2 && <p className="text-sm text-gray-700">{team.player2.fullName}</p>}
    </div>
  )

  return (
    <PortalDialog open onClose={onClose} width="max-w-md">
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Match Details</h3>
            <p className="text-xs text-gray-500 mt-0.5">{moment(match.eventDate).format('MMMM D, YYYY')}</p>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Event info */}
        <div className="space-y-1.5 mb-4">
          {match.eventName && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">Event</span>
              <span className="text-xs font-medium text-gray-800">{match.eventName}</span>
            </div>
          )}
          {match.location && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">Location</span>
              <span className="text-xs text-gray-800">{match.location}</span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Format</span>
            <span className={`text-xs font-medium ${match.eventFormat === 'DOUBLES' ? 'text-blue-600' : 'text-amber-600'}`}>
              {match.eventFormat}
            </span>
          </div>
          {match.displayIdentity && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">Match ID</span>
              <button onClick={() => onCopyId(match)} className="text-xs text-primary-600 hover:text-primary-500">
                {match.displayIdentity}
              </button>
            </div>
          )}
        </div>

        {/* Teams */}
        <div className="flex gap-2 mb-4">
          {renderTeamDetail(team1, 'Team 1', team1.winner)}
          {renderTeamDetail(team2, 'Team 2', team2.winner)}
        </div>

        {/* Score */}
        <div className="flex justify-center gap-3 py-3 bg-gray-50 border border-gray-200 mb-4">
          {games.map(([g1, g2], i) => (
            <div key={i} className="text-center">
              <span className="text-[10px] text-gray-400 block mb-0.5">G{i + 1}</span>
              <span className="text-sm font-mono">
                <span className={g1 > g2 ? 'font-bold text-gray-900' : 'text-gray-400'}>{g1}</span>
                <span className="text-gray-300 mx-0.5">-</span>
                <span className={g2 > g1 ? 'font-bold text-gray-900' : 'text-gray-400'}>{g2}</span>
              </span>
            </div>
          ))}
        </div>

        {/* Actions */}
        {isDirector && (
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => onDelete(match)}
              className="px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 hover:bg-red-50 transition-colors"
            >
              Delete
            </button>
            <button
              onClick={() => onEdit(match)}
              className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
            >
              Edit Match
            </button>
          </div>
        )}
      </div>
    </PortalDialog>
  )
}

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   Edit Match Dialog
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
const EditMatchDialog = ({
  match,
  clubId,
  isEditing,
  editClubMatch,
  user,
  onClose,
  onSuccess,
}: {
  match: Match | null
  clubId: number
  isEditing: boolean
  editClubMatch: (payload: any) => Promise<any>
  user: any
  onClose: () => void
  onSuccess: () => void
}) => {
  const [eventName, setEventName] = useState('')
  const [eventDate, setEventDate] = useState('')
  const [scores, setScores] = useState<[string, string][]>([])

  // Reset form when match changes
  useEffect(() => {
    if (match) {
      setEventName(match.eventName || '')
      setEventDate(moment(match.eventDate).format('YYYY-MM-DD'))

      const [t1, t2] = [...match.teams].sort((a, b) => a.serial - b.serial)
      const gameScores: [string, string][] = []
      for (let i = 1; i <= 5; i++) {
        const g1 = (t1 as any)[`game${i}`]
        const g2 = (t2 as any)[`game${i}`]
        if (g1 !== undefined && g1 !== -1 && g2 !== undefined && g2 !== -1) {
          gameScores.push([String(g1), String(g2)])
        }
      }
      // Ensure at least 1 game row
      if (gameScores.length === 0) gameScores.push(['', ''])
      setScores(gameScores)
    }
  }, [match])

  if (!match) return null

  const [team1, team2] = [...match.teams].sort((a, b) => a.serial - b.serial)

  const updateScore = (gameIdx: number, teamIdx: 0 | 1, value: string) => {
    if (!/^\d{0,2}$/.test(value)) return
    setScores((prev) => {
      const next = [...prev] as [string, string][]
      next[gameIdx] = [...next[gameIdx]] as [string, string]
      next[gameIdx][teamIdx] = value
      return next
    })
  }

  const addGame = () => {
    if (scores.length < 5) setScores((prev) => [...prev, ['', '']])
  }

  const removeGame = (idx: number) => {
    if (scores.length > 1) setScores((prev) => prev.filter((_, i) => i !== idx))
  }

  const handleSave = async () => {
    // Build game scores — pad to 5 games with -1
    const gameScores = Array.from({ length: 5 }, (_, i) => {
      const pair = scores[i]
      return pair && pair[0] !== '' && pair[1] !== ''
        ? [Number(pair[0]), Number(pair[1])]
        : [-1, -1]
    })

    // Determine winners per game
    let t1Wins = 0, t2Wins = 0
    gameScores.forEach(([g1, g2]) => {
      if (g1 > 0 && g2 > 0) {
        if (g1 > g2) t1Wins++
        else if (g2 > g1) t2Wins++
      }
    })

    const payload = {
      clubId: String(clubId),
      matchId: String(match.matchId),
      eventName,
      eventDate: moment(eventDate).format('YYYY-MM-DD'),
      location: match.location,
      format: match.eventFormat,
      matchType: match.matchType || 'SIDE_ONLY',
      matchSource: 'CLUB',
      reason: 'Match Edited by Director',
      requestedBy: user?.referralCode,
      teams: [
        {
          id: team1.id,
          game1: gameScores[0][0], game2: gameScores[1][0], game3: gameScores[2][0], game4: gameScores[3][0], game5: gameScores[4][0],
          player1: team1.player1.id,
          player2: team1.player2?.id || '',
          winner: t1Wins > t2Wins,
        },
        {
          id: team2.id,
          game1: gameScores[0][1], game2: gameScores[1][1], game3: gameScores[2][1], game4: gameScores[3][1], game5: gameScores[4][1],
          player1: team2.player1.id,
          player2: team2.player2?.id || '',
          winner: t2Wins > t1Wins,
        },
      ],
    }

    try {
      await editClubMatch(payload)
      toast.success('Match updated')
      onSuccess()
    } catch {
      toast.error('Failed to update match')
    }
  }

  return (
    <PortalDialog open onClose={onClose} width="max-w-md">
      <div className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-900">Edit Match</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Event info */}
        <div className="space-y-3 mb-4">
          <div>
            <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wider block mb-1">Event Name</label>
            <input
              type="text"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              className="w-full px-2.5 py-1.5 border border-gray-200 text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
          </div>
          <div>
            <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wider block mb-1">Date</label>
            <input
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              className="w-full px-2.5 py-1.5 border border-gray-200 text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-primary-400"
            />
          </div>
        </div>

        {/* Players (read-only) */}
        <div className="flex gap-2 mb-4">
          <div className="flex-1 p-2 bg-gray-50 border border-gray-200">
            <span className="text-[10px] text-gray-400 uppercase block mb-0.5">Team 1</span>
            <p className="text-xs font-medium text-gray-800">{team1.player1.fullName}</p>
            {team1.player2 && <p className="text-xs text-gray-600">{team1.player2.fullName}</p>}
          </div>
          <div className="flex-1 p-2 bg-gray-50 border border-gray-200">
            <span className="text-[10px] text-gray-400 uppercase block mb-0.5">Team 2</span>
            <p className="text-xs font-medium text-gray-800">{team2.player1.fullName}</p>
            {team2.player2 && <p className="text-xs text-gray-600">{team2.player2.fullName}</p>}
          </div>
        </div>

        {/* Scores */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">Scores</span>
            {scores.length < 5 && (
              <button onClick={addGame} className="text-[10px] text-primary-600 hover:text-primary-500">
                + Add game
              </button>
            )}
          </div>
          <div className="space-y-1.5">
            {scores.map(([g1, g2], idx) => (
              <div key={idx} className="flex items-center gap-2">
                <span className="text-[10px] text-gray-400 w-6">G{idx + 1}</span>
                <input
                  type="text"
                  value={g1}
                  onChange={(e) => updateScore(idx, 0, e.target.value)}
                  className="w-14 px-2 py-1 border border-gray-200 text-sm text-center text-gray-900 focus:outline-none focus:ring-1 focus:ring-primary-400"
                  maxLength={2}
                  placeholder="T1"
                />
                <span className="text-gray-300">-</span>
                <input
                  type="text"
                  value={g2}
                  onChange={(e) => updateScore(idx, 1, e.target.value)}
                  className="w-14 px-2 py-1 border border-gray-200 text-sm text-center text-gray-900 focus:outline-none focus:ring-1 focus:ring-primary-400"
                  maxLength={2}
                  placeholder="T2"
                />
                {scores.length > 1 && (
                  <button onClick={() => removeGame(idx)} className="text-gray-400 hover:text-red-500 p-0.5">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isEditing}
            className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 transition-colors"
          >
            {isEditing ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </PortalDialog>
  )
}
